<template>
  <div v-show="!homeScreenStatus && !exerciseCompleted && !gameCompleted" class="game">
    <!-- Points & Lives -->
    <div class="game__points">
      <p>Points: {{ playerScore }}</p>
    </div>
    <div class="game__lives">
      <ul>
        <li v-for="life in currentLives" :key="life">
          <img class="life" src="@/assets/practices/expanded_form/life.svg" alt="life" />
        </li>
        <li v-for="life in maxLives - currentLives" :key="life + 10">
          <img class="life" src="@/assets/practices/expanded_form/life_dim.svg" alt="life_dim" />
        </li>
      </ul>
    </div>

    <!-- Question -->
    <div v-show="!explanationStatus" :class="{ game__question: true, 'remove-question': disableDraggable }">
      <img
        class="game__question--background"
        src="@/assets/practices/expanded_form/scroll_question.svg"
        alt="scroll_question"
      />
      <div class="game__question__container">
        <!-- <p
          class="game__question__container--text"
        >Drag the egg with the correct answer into the dragon's nest.</p>-->
        <p class="game__question__container--text">Drag the correct answer.</p>
        <div class="game__question__container--problem">{{ fiveExercises[currentExercise].question.problem }}</div>
      </div>
    </div>

    <!-- Dragon & Nest -->
    <div v-show="!explanationStatus" id="dragon-game"></div>
    <div v-show="!explanationStatus" class="game__nest">
      <div id="dropzone"></div>
      <img
        v-show="!explanationStatus"
        id="nest"
        class="game__nest--egg"
        src="@/assets/practices/expanded_form/egg_nest.svg"
        alt="nest"
      />
      <div id="egg-burned"></div>
      <div id="egg-golden"></div>
    </div>

    <!-- Eggs -->
    <div v-show="!explanationStatus" :key="render" class="game__egg-grid">
      <Egg
        id="egg-draggable-0"
        :class="{ draggable: true, 'disable-draggable': disableDraggable }"
        data-value="0"
        egg-color="egg_sparkles"
        :egg-number="fiveExercises[currentExercise].options[0].number"
      />
      <Egg
        id="egg-draggable-1"
        :class="{ draggable: true, 'disable-draggable': disableDraggable }"
        data-value="1"
        egg-color="egg_flames"
        :egg-number="fiveExercises[currentExercise].options[1].number"
      />
      <Egg
        id="egg-draggable-2"
        :class="{ draggable: true, 'disable-draggable': disableDraggable }"
        data-value="2"
        egg-color="egg_wind"
        :egg-number="fiveExercises[currentExercise].options[2].number"
      />
      <Egg
        id="egg-draggable-3"
        :class="{ draggable: true, 'disable-draggable': disableDraggable }"
        data-value="3"
        egg-color="egg_stars"
        :egg-number="fiveExercises[currentExercise].options[3].number"
      />
    </div>

    <!-- Game Explanation -->
    <div v-show="explanationStatus" class="game__explanation">
      <div v-if="correctStatus" class="game__explanation__container">
        <p>Good job!</p>
      </div>
      <div v-else class="game__explanation__container">
        <p>Your answer was: {{ userChosenAnswer }}</p>
        <p>The correct answer is: {{ fiveExercises[currentExercise].question.answer }}.</p>
        <div class="game__explanation__example">
          Step 1:
          <p>
            {{ fiveExercises[currentExercise].question.numbers[0] }} +
            {{ fiveExercises[currentExercise].question.numbers[1] }} =
            {{
              fiveExercises[currentExercise].question.numbers[0] + fiveExercises[currentExercise].question.numbers[1]
            }}
          </p>
        </div>
        <div class="game__explanation__example">
          Step 2:
          <p>
            {{
              fiveExercises[currentExercise].question.numbers[0] + fiveExercises[currentExercise].question.numbers[1]
            }}
            + {{ fiveExercises[currentExercise].question.numbers[2] }} =
            {{ fiveExercises[currentExercise].question.answer }}
          </p>
        </div>
        <div class="game__explanation__example">
          Then:
          <p>
            {{ fiveExercises[currentExercise].question.numbers[0] }} +
            {{ fiveExercises[currentExercise].question.numbers[1] }} +
            {{ fiveExercises[currentExercise].question.numbers[2] }} =
            {{ fiveExercises[currentExercise].question.answer }}
          </p>
        </div>
      </div>
      <button v-if="!correctStatus" class="game__explanation__container--button" @click="clickContinue">Got It!</button>
      <button v-else class="game__explanation__container--button" @click="clickContinue">Continue</button>
    </div>

    <!-- Correct / Incorrect Scroll Animations -->
    <div v-show="disableDraggable" id="correct-scroll"></div>
    <div v-show="disableDraggable" id="incorrect-scroll"></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
// import { Howl } from 'howler';
import interact from 'interactjs';
import lottie from 'lottie-web/build/player/lottie';
import { setTimeout } from 'timers';
import exercises from '@/assets/practices/data/expanded_form/exercises.json';
import sounds from '@/packages/sounds/index.js';

export default {
  name: 'Game',
  components: {
    Egg: () => import('@/components/practices/expanded_form/Egg.vue'),
  },
  data: () => ({
    egg: [
      { id: 'egg-draggable-0', position: { x: 0, y: 0 } },
      { id: 'egg-draggable-1', position: { x: 0, y: 0 } },
      { id: 'egg-draggable-2', position: { x: 0, y: 0 } },
      { id: 'egg-draggable-3', position: { x: 0, y: 0 } },
    ],
    disableDraggable: false,
    exercises,
    fiveExercises: [],
    render: 0,
    userChosenAnswer: null,
  }),
  computed: {
    ...mapGetters({
      correctStatus: 'expandedForm/correctStatus',
      currentExercise: 'expandedForm/currentExercise',
      currentLives: 'expandedForm/currentLives',
      exerciseCompleted: 'expandedForm/exerciseCompleted',
      explanationStatus: 'expandedForm/explanationStatus',
      gameCompleted: 'expandedForm/gameCompleted',
      homeScreenStatus: 'expandedForm/homeScreenStatus',
      maxLives: 'expandedForm/maxLives',
      muteStatus: 'settings/muteStatus',
      playerScore: 'expandedForm/playerScore',
    }),
  },
  created() {
    this.fiveExercises = this.getRandom(this.exercises, 5);
  },
  mounted() {
    this.checkMuteStatus(this.muteStatus);
    const self = this;
    const dragonGameAnimation = lottie.loadAnimation({
      container: document.getElementById('dragon-game'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: '/assets/practices/expanded_form/animations/dragon_fire.json',
    });
    const correctGameAnimation = lottie.loadAnimation({
      container: document.getElementById('correct-scroll'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: '/assets/practices/expanded_form/animations/scroll_correct.json',
    });
    const incorrectGameAnimation = lottie.loadAnimation({
      container: document.getElementById('incorrect-scroll'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: '/assets/practices/expanded_form/animations/scroll_incorrect.json',
    });
    const goldenEggGameAnimation = lottie.loadAnimation({
      container: document.getElementById('egg-golden'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: '/assets/practices/expanded_form/animations/egg_golden.json',
    });
    const burnedEggGameAnimation = lottie.loadAnimation({
      container: document.getElementById('egg-burned'),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: '/assets/practices/expanded_form/animations/egg_burned.json',
    });
    burnedEggGameAnimation.setSpeed(1.25);

    setTimeout(() => {
      const dropzoneX = document.getElementById('dropzone').getBoundingClientRect().x;
      const dropzoneY = document.getElementById('dropzone').getBoundingClientRect().y;
      interact('.draggable').draggable({
        max: 1,
        modifiers: [
          interact.modifiers.restrict({
            restriction: '.main',
            elementRect: {
              top: 0,
              left: 0,
              bottom: 1,
              right: 1,
            },
          }),
          interact.modifiers.snap({
            targets: [{ x: dropzoneX, y: dropzoneY }],
            range: 50,
            relativePoints: [{ x: 0, y: 0 }],
          }),
        ],
        // onstart(event) {
        //   // Use this to fire events when initiating drag.
        // },
        // onend(event) {
        //   // Use this to fire events when ending drag.
        // },
        onmove(event) {
          if (event.target.id === self.egg[0].id) {
            self.egg[0].position.x += event.dx;
            self.egg[0].position.y += event.dy;
            event.target.style.transform = `translate(${self.egg[0].position.x}px, ${self.egg[0].position.y}px)`;
          }
          if (event.target.id === self.egg[1].id) {
            self.egg[1].position.x += event.dx;
            self.egg[1].position.y += event.dy;
            event.target.style.transform = `translate(${self.egg[1].position.x}px, ${self.egg[1].position.y}px)`;
          }
          if (event.target.id === self.egg[2].id) {
            self.egg[2].position.x += event.dx;
            self.egg[2].position.y += event.dy;
            event.target.style.transform = `translate(${self.egg[2].position.x}px, ${self.egg[2].position.y}px)`;
          }
          if (event.target.id === self.egg[3].id) {
            self.egg[3].position.x += event.dx;
            self.egg[3].position.y += event.dy;
            event.target.style.transform = `translate(${self.egg[3].position.x}px, ${self.egg[3].position.y}px)`;
          }
        },
        // eslint-disable-next-line no-unused-vars
        cursorChecker: (action, interatable, element) => {
          switch (action.axis) {
            case 'x':
              return 'ew-resize';
            case 'y':
              return 'ns-resize';
            case 'xy':
              return 'grab';
          }
        },
      });
    }, 750);

    interact('#dropzone').dropzone({
      overlap: 0.85,
      ondrop(event) {
        dragonGameAnimation.play();
        self.disableDraggable = true;
        // Allows animation of dragon to be in front of eggs.
        document.getElementById('dragon-game').style.zIndex = '2';
        self.userChosenAnswer =
          self.fiveExercises[self.currentExercise].options[event.relatedTarget.getAttribute('data-value')].number;
        if (self.fiveExercises[self.currentExercise].options[event.relatedTarget.getAttribute('data-value')].answer) {
          setTimeout(() => {
            document.getElementById('egg-golden').style.display = 'block';
            document.getElementById('nest').style.opacity = 0;
            document.getElementById(event.relatedTarget.id).style.opacity = 0;
            goldenEggGameAnimation.play();
            self.$store.state.expandedForm.playerScore += 300;
            correctGameAnimation.play();
          }, 4000);
          sounds.expandedFormEggGolden.play();
          self.$store.state.expandedForm.correctStatus = true;
          setTimeout(() => {
            self.$store.state.expandedForm.explanationStatus = true;
            self.$confetti.start({
              defaultSize: 10,
              particlesPerFrame: 0.15,
              particles: [
                {
                  type: 'rect',
                },
                {
                  type: 'circle',
                },
                {
                  type: 'image',
                  size: 70,
                  url: '/assets/practices/global/confetti/correct_cool.svg',
                },
              ],
            });
          }, 8500);
        } else {
          setTimeout(() => {
            document.getElementById('egg-burned').style.display = 'block';
            document.getElementById('nest').style.opacity = 0;
            document.getElementById(event.relatedTarget.id).style.opacity = 0;
            sounds.expandedFormEggBurned.rate(1.3);
            sounds.expandedFormEggBurned.play();
            burnedEggGameAnimation.play();
            self.$store.state.expandedForm.currentLives -= 1;
            incorrectGameAnimation.play();
          }, 4500);
          sounds.expandedFormDragonFire.play();
          setTimeout(() => {
            self.$store.state.expandedForm.explanationStatus = true;
            self.$confetti.start({
              defaultSize: 10,
              particlesPerFrame: 0.1,
              defaultDropRate: 10,
              particles: [
                {
                  type: 'image',
                  size: 20,
                  dropRate: 5,
                  url: '/assets/practices/global/confetti/incorrect_cross_heavy.svg',
                },
              ],
            });
          }, 8500);
        }
        // Reverts z-index of dragon when animation ends.
        setTimeout(() => {
          document.getElementById('dragon-game').style.zIndex = '0';
        }, 6000);
        // Resets
        setTimeout(() => {
          self.disableDraggable = false;
          dragonGameAnimation.stop();
          correctGameAnimation.stop();
          incorrectGameAnimation.stop();
          document.getElementById('egg-burned').style.display = 'none';
          document.getElementById('egg-golden').style.display = 'none';
          document.getElementById('nest').style.opacity = 1;
          burnedEggGameAnimation.stop();
          goldenEggGameAnimation.stop();
        }, 8500);
      },
    });
  },
  methods: {
    checkGameOver() {
      if (
        (this.$store.state.expandedForm.exerciseCompleted && this.$store.state.expandedForm.currentLives <= 0) ||
        (this.$store.state.expandedForm.exerciseCompleted && this.$store.state.expandedForm.currentExercise >= 4)
      ) {
        this.$store.state.expandedForm.gameCompleted = true;
      } else {
        this.$store.state.expandedForm.currentExercise += 1;
      }
    },
    checkMuteStatus(status) {
      if (status) {
        sounds.mute(true);
      } else {
        sounds.mute(false);
      }
    },
    clickContinue() {
      sounds.globalButton.play();
      this.$store.state.expandedForm.explanationStatus = false;
      this.$store.state.expandedForm.exerciseCompleted = true;
      this.checkGameOver();
      if (!this.$store.state.expandedForm.gameCompleted) {
        this.$store.state.expandedForm.exerciseCompleted = false;
      }
      this.$confetti.stop();
      this.eggResetPositions();
      this.render += 1;
      this.$store.state.expandedForm.correctStatus = false;
    },
    eggResetPositions() {
      this.egg[0].position = { x: 0, y: 0 };
      this.egg[1].position = { x: 0, y: 0 };
      this.egg[2].position = { x: 0, y: 0 };
      this.egg[3].position = { x: 0, y: 0 };
    },
    getRandom(arr, n) {
      const result = new Array(n);
      let len = arr.length;
      const taken = new Array(len);
      if (n > len) throw new RangeError('getRandom: more elements taken than available');
      while (n--) {
        const x = Math.floor(Math.random() * len);
        result[n] = arr[x in taken ? taken[x] : x];
        taken[x] = --len in taken ? taken[len] : len;
      }
      return result;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/main.scss';

.game {
  @include flex-center;
  flex-direction: column;

  &__points,
  &__lives {
    @include flex-center;
    background-color: #cbfdffbb;
    border: 0.75vh solid white;
    border-radius: 5vw;
    height: 9vh;
    padding: 0 2vw;
    position: absolute;
    top: 2vh;
  }

  &__points {
    color: $expanded-form-color-alt-second;
    font-size: 3vw;
    font-family: 'VanillaExtractRegular';
    left: 2vw;
  }

  &__lives {
    right: 2vw;

    ul {
      @include flex-center;
      list-style: none;

      li {
        margin: 1vh 1vw;

        .life {
          height: 6vh;
          margin-top: 1vh;
        }
      }
    }
  }

  #dragon-game {
    position: absolute;
    transform: translate(-17vh, -10vh);
    left: 18vh;
    width: 110vh;
    z-index: 0;
  }

  &__nest {
    @include flex-center;
    position: absolute;
    height: 42vh;
    left: 18vh;
    transform: translate(35vh, -7vh);

    &--egg {
      height: 100%;
      opacity: 1;
    }
  }

  &__question {
    @include flex-center;
    position: absolute;
    top: 14vh;
    right: -1%;
    width: 40vw;

    &--background {
      height: 100%;
      width: 100%;
    }

    &__container {
      position: absolute;
      text-align: center;
      transform: translate(3%, 2%);
      width: 85%;

      &--text {
        font-size: 2.2vw;
        line-height: 1.3;
        margin-bottom: 0.5em;
      }

      &--problem {
        font-size: 4vw;
      }
    }
  }

  &__egg-grid {
    align-items: center;
    bottom: 5vh;
    display: grid;
    grid-template: 'column1 column2 column3 column4';
    grid-template-columns: repeat(4, min-content);
    grid-gap: 2.5vw;
    justify-content: center;
    position: absolute;

    .column1 {
      grid-area: column1;
    }
    .column2 {
      grid-area: column2;
    }
    .column3 {
      grid-area: column3;
    }
    .column4 {
      grid-area: column3;
    }
  }
  &__explanation {
    @include flex-center;
    background-color: #00000070;
    flex-direction: column;
    height: 100vh;
    text-align: center;
    width: 100vw;

    &__example {
      @include flex-center;
      flex-direction: row;
      margin: 0.5em 0;

      &:first-child {
        margin-top: 1em;
      }

      &:last-child {
        margin-bottom: 0.5em;
      }

      p {
        background-color: #cbfdffbb;
        border: 0.75vh solid white;
        border-radius: 5vw;
        color: $expanded-form-color-alt-second;
        margin-left: 0.5em;
        padding: 0.25em 0.75em;
        // font-size: 3vw;
      }
    }

    &__container {
      @include flex-center;
      flex-direction: column;
      text-align: center;
      width: 90%;

      // &--text {
      p {
        font-size: 2.5vw;
        line-height: 1.5;
        // margin-bottom: 0.5em;
      }

      &--button {
        background-color: $expanded-form-color-main;
        border: none;
        border-radius: 50vh;
        color: $expanded-form-color-alt-third;
        font-size: 3vw;
        margin-top: 0.5em;
        padding: 0.25em 0.75em;
      }
    }
  }

  .disable-draggable {
    pointer-events: none;
  }

  .remove-question {
    animation: remove-question 2s forwards;
  }

  #correct-scroll,
  #incorrect-scroll {
    height: 30vh;
    position: absolute;
    right: 0;
    top: 20vh;
    width: calc(30vh * 2.65);
    z-index: 3;
  }

  #egg-draggable-0,
  #egg-draggable-1,
  #egg-draggable-2,
  #egg-draggable-3 {
    touch-action: none;
    user-select: none;
  }

  #dropzone {
    height: 32vh;
    position: absolute;
    transform: translate(-0.25vh, -5vh);
    width: 25vh;
  }

  #egg-burned,
  #egg-golden {
    display: none;
    height: 50vh;
    position: absolute;
    transform: translate(-0.75vh, -3.5vh);
    width: 37vh;
  }
}

@keyframes remove-question {
  0% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(-5vw, 0);
  }
  100% {
    transform: translate(75vw, 0);
  }
}

@media (max-aspect-ratio: 8/5) {
  .game {
    #dragon-game {
      left: 15vh;
    }

    &__points {
      p {
        margin-top: 1vh;
      }
    }

    &__nest {
      left: 15vh;
      transform: translate(32vh, -7vh);
    }

    #correct-scroll,
    #incorrect-scroll {
      height: 20vh;
      top: 25vh;
      width: calc(20vh * 2.65);
    }
  }
}
</style>
